import { useGridStateModule as useGridStateModuleAzure } from "@ls/grid-state-module-azure";
import { useGridStateModule as useGridStateModuleLegacy } from "@ls/grid-state-module";
import { isDefined, compareValues } from "@ls/js-utils";
import Grid from "@ls/react-grid";
import NoBenchmarkRowData from "components/AccountGrid/PseudoBenchmark";
import StatusBar from "components/AccountGrid/StatusBar";
import { produce } from "immer";
import React, { memo, useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  selectBandedRows,
  selectDisableCustomGroupSort,
  selectGridFontSize,
  selectGridHeaderHeight,
  selectGridRowHeight,
  selectGridVerticalLines,
  selectHideGridSideBar,
} from "state/app/selector";
import { selectAccountOrders } from "state/attributes/selectors";
import { useContextMenuEffect, useGridInstance } from "state/grid";
import SelectionState from "state/selections/SelectionState";
import { selectDisplayBenchmarkValue } from "state/selections/selector";
import styles from "./AccountGrid.module.css";
import "./style.css";
import { useAllColumnDefinitions, useAllRowData } from "state/grid/hooks";
import appTalkSentMessage from "appTalkSentMessage";

const { in2AppTalk } = window;

const getRowStyle = () => {
  return {
    cursor: "pointer",
  };
};

const X_getStaticRow = (row) => {
  return row.isBenchmark;
};
const AccountGrid = () => {
  const displayBenchmarksValue = useSelector(
    selectDisplayBenchmarkValue(SelectionState.COMMITTED)
  );
  const accountOrders = useSelector(selectAccountOrders);
  const disableGroupingSort = useSelector(selectDisableCustomGroupSort);
  const columnDefs = useAllColumnDefinitions();
  const allRowData = useAllRowData();
  const hideGridSideBar = useSelector(selectHideGridSideBar);

  const [grid, setGridInstance] = useGridInstance();
  const sortedRowData = useMemo(() => {
    if (!displayBenchmarksValue) {
      //no benchmarks. just return rows as is
      const temp = [...allRowData];
      temp.sort(compareValues("order"));
      return temp;
    } else if (disableGroupingSort) {
      // show benchmarks without custom group sorting. needs to be sorted by largest bmk to smallest with no bmk last
      //get map of all rows, acc rows, and bmk rows by their entity ids for fast look ups
      const allRowsByEntityId = new Map();
      const accRowsByEntityId = new Map();
      const bmkRowsByEntityId = new Map();
      const accRowsGroupedByBenchmarkId = new Map();
      allRowsByEntityId.set(NoBenchmarkRowData.entityId, NoBenchmarkRowData);
      bmkRowsByEntityId.set(NoBenchmarkRowData.entityId, NoBenchmarkRowData);
      allRowData.forEach((row) => {
        let { entityId, benchmarkId } = row;
        allRowsByEntityId.set(entityId, row);
        if (row.isBenchmark) {
          bmkRowsByEntityId.set(entityId, row);
        } else {
          accRowsByEntityId.set(entityId, row);

          if (!benchmarkId) {
            benchmarkId = NoBenchmarkRowData.entityId;
          }
          if (!accRowsGroupedByBenchmarkId.has(benchmarkId)) {
            accRowsGroupedByBenchmarkId.set(benchmarkId, []);
          }
          accRowsGroupedByBenchmarkId.get(benchmarkId).push(row);
        }
      });

      //sort benchmark ids by the 'size' of each benchmark, with "No Benchmark" always at the end
      const sortedBenchmarkIds = [...accRowsGroupedByBenchmarkId.keys()].sort(
        (benchmarkId1, benchmarkId2) => {
          if (
            benchmarkId1 === undefined ||
            benchmarkId2 === NoBenchmarkRowData.entityId
          ) {
            return -1;
          }
          if (
            benchmarkId2 === undefined ||
            benchmarkId1 === NoBenchmarkRowData.entityId
          ) {
            return 1;
          }
          return (
            accRowsGroupedByBenchmarkId.get(benchmarkId2).length -
            accRowsGroupedByBenchmarkId.get(benchmarkId1).length
          );
        }
      );

      //iterate sorted benchmarks (largest to smallest) and add each bmk row + its accounts to rows
      const rows = [];
      sortedBenchmarkIds.forEach((benchmarkId) => {
        const benchmarkRowData = bmkRowsByEntityId.get(benchmarkId);
        const accRows = accRowsGroupedByBenchmarkId.get(benchmarkId);
        if (benchmarkRowData && accRows) {
          rows.push(...[benchmarkRowData, ...accRows]);
        }
      });

      return rows;
    } else {
      // show benchmarks with custom group sorting.
      //get map of all rows, acc rows, and bmk rows by their entity ids for fast look ups
      const allRowsByEntityId = new Map();
      const accRowsByEntityId = new Map();
      const bmkRowsByEntityId = new Map();
      allRowsByEntityId.set(NoBenchmarkRowData.entityId, NoBenchmarkRowData);
      bmkRowsByEntityId.set(NoBenchmarkRowData.entityId, NoBenchmarkRowData);
      allRowData.forEach((_row) => {
        const row = { ..._row };
        const { entityId } = row;
        allRowsByEntityId.set(entityId, row);
        if (row.isBenchmark) {
          bmkRowsByEntityId.set(entityId, row);
        } else {
          accRowsByEntityId.set(entityId, row);
        }
      });

      //create pushAccRow function that adds acc rows the list. will intersperse benchmark rows as needed
      const rows = [];
      let prevBmkEntityId = null;
      const pushAccRow = (accRow) => {
        const benchmarkId = accRow.benchmarkId || NoBenchmarkRowData.entityId;
        const bmkRow = allRowsByEntityId.get(benchmarkId);
        if (prevBmkEntityId === bmkRow.entityId) {
          //same bmk as before, keep pushin'
          rows.push(accRow);
        } else {
          //different bmk then the last one, 'interlace' benchmark row
          prevBmkEntityId = bmkRow.entityId;
          rows.push(bmkRow);
          rows.push(accRow);
        }
      };

      //Start by ordered entity ids and add each corresponding row the the row array.
      //keep track of remaining account rows as well (ones with no order information)
      const remainingAccRowsByEntityId = new Map(accRowsByEntityId);
      accountOrders.forEach((entityId) => {
        remainingAccRowsByEntityId.delete(entityId);
        const accRow = accRowsByEntityId.get(entityId);
        if (isDefined(accRow)) {
          pushAccRow(accRow);
        }
      });

      //Now sort remaining account rows by their benchmark account count
      const remainingAccRows = [...remainingAccRowsByEntityId.values()];
      remainingAccRows.sort((accRow1, accRow2) => {
        const bmk1 = bmkRowsByEntityId.get(accRow1.benchmarkId);
        const bmk2 = bmkRowsByEntityId.get(accRow2.benchmarkId);
        if (bmk1 === bmk2) {
          return 0;
        }

        if (bmk1 == null) {
          return 1;
        }

        if (bmk2 == null) {
          return -1;
        }

        return bmk2.accountCount - bmk1.accountCount;
      });

      //Add remaining account rows to the row array
      remainingAccRows.forEach((accRow) => {
        pushAccRow(accRow);
      });

      return rows;
    }
  }, [allRowData, displayBenchmarksValue, accountOrders, disableGroupingSort]);

  const rowData = useMemo(() => {
    return sortedRowData.map(
      produce((data, idx) => {
        data.rowId = `${data.entityId},${idx}`; //necessary since certain rows (like benchmark rows) can appear multiple times.
      })
    );
  }, [sortedRowData]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      suppressMenu: true,
      defaultWidth: 90,
      floatingFilter: true,
    }),
    []
  );

  const onRowSelected = useCallback(() => {
    const selectedRows = grid.api.getSelectedRows();
    if (!in2AppTalk) {
      console.error("in2AppTalk not imported.");
      return;
    }
    if (selectedRows.length === 1) {
      //auto emmit in2 message if and only if there's one account
      const { entityId, name } = selectedRows[0];
      const msg = {};
      msg.in2type = in2AppTalk.IN2_DATA_TYPE.ACCOUNT;
      msg.insitetype = in2AppTalk.INSITE_DATA_TYPE.ACCOUNT;
      msg.uniqueIdType = in2AppTalk.IN2_ID_TYPE.ENTITY_ID;
      msg.name = name;
      msg.uniqueIdVal = entityId;
      appTalkSentMessage.current = msg;
      in2AppTalk
        .getInstance()
        .publish(in2AppTalk.MSG_TYPE.PRIMARY_ITEM_CHANGE, msg);

      console.log(`Sent in2AppTalk message `, msg);
    }
  }, [grid]);

  useContextMenuEffect();

  const [selectedCellRanges, setSelectedCellRanges] = useState([]);
  const onRangeSelectionChanged = useCallback(({ api }) => {
    const cellRanges = [...api.getCellRanges()];
    setSelectedCellRanges(cellRanges);
  }, []);

  const [rowNodesAfterFilter, setRowNodesAfterFilter] = useState([]);

  const onModelUpdated = useCallback(({ api }) => {
    let rowNodes = [];
    api.forEachNodeAfterFilter((rowNode) => {
      rowNodes.push(rowNode);
    });
    setRowNodesAfterFilter(rowNodes);
  }, []);

  const getRowNodeId = useCallback(({ entityId, rowId }) => {
    return rowId ?? entityId;
  }, []);

  const bandedRows = useSelector(selectBandedRows);
  const enableVerticalGridLines = useSelector(selectGridVerticalLines);
  const fontSize = useSelector(selectGridFontSize);
  const rowHeight = useSelector(selectGridRowHeight);
  const headerHeight = useSelector(selectGridHeaderHeight);

  const gridStateModuleAzure = useGridStateModuleAzure();
  const gridStateModuleLegacy = useGridStateModuleLegacy();

  const gridStateModule =
    process.env.REACT_APP_HOST_ENV === "AZURE"
      ? gridStateModuleAzure
      : gridStateModuleLegacy;
  if (process.env.REACT_APP_IN2_ENV === "DEV") {
    console.log("columnDefs", columnDefs);
    console.log("sortedRowData", sortedRowData);
  }
  return (
    <div className={styles.root}>
      <div className="gridWrapperRoot">
        <Grid
          rowData={rowData}
          columnDefs={columnDefs}
          onGridReady={setGridInstance}
          defaultColDef={defaultColDef}
          immutableData
          getRowNodeId={getRowNodeId}
          getRowStyle={getRowStyle}
          bandedRows={bandedRows}
          onRowSelected={onRowSelected}
          suppressContextMenu={false}
          suppressCopyRowsToClipboard
          onRangeSelectionChanged={onRangeSelectionChanged}
          onModelUpdated={onModelUpdated}
          // suppressFieldDotNotation // needed for some columns that has dot (.) character in their field
          // commented it out for cf rows. need to figure out issue columns that has the dot character (i think they're pivots)
          headerHeight={headerHeight}
          enableVerticalGridLines={enableVerticalGridLines}
          fontSize={fontSize}
          rowHeight={rowHeight}
          gridStateModule={gridStateModule}
          groupDefaultExpanded={-1}
          rowDataChangeDetectionStrategy="IdentityCheck"
          rowSelection="multiple"
          suppressCellSelection
          excludeChildrenWhenTreeDataFiltering
          enableRangeSelection
          enableActionButtons
          animateRows={false}
          X_hideSideBar={hideGridSideBar}
          X_getStaticRow={X_getStaticRow}
          X_exportURL={process.env.REACT_APP_EXPORT_SERVICE || null}
        />
      </div>
      <StatusBar
        selectedCellRanges={selectedCellRanges}
        rowNodesAfterFilter={rowNodesAfterFilter}
        grid={grid}
      />
    </div>
  );
};

export default memo(AccountGrid);
